/* ------------------------------------------------------------------ */
/*  PRD.299 — APPOINTMENT_BOOKED dispatch client                       */
/*                                                                     */
/*  web-admin creates appointments offline-first: the row lands in     */
/*  local SQLite immediately but only reaches Postgres when PowerSync  */
/*  uploads it. The backend reads the appointment from Postgres, so    */
/*  this client waits for the upload queue to drain before calling —   */
/*  otherwise the endpoint would look at a row that does not exist     */
/*  yet and answer `pending`.                                          */
/*                                                                     */
/*  Nothing here can affect the booking: the appointment is already    */
/*  saved before any of this runs.                                     */
/* ------------------------------------------------------------------ */

export type AppointmentBookedOutcome =
  | { status: 'sent' }
  | { status: 'queued' }
  | { status: 'skipped'; reason: string }
  | { status: 'cancelled' }
  /** Sync never drained, or the endpoint stayed `pending` — retryable. */
  | { status: 'timeout' }
  | { status: 'error'; message: string };

export interface DispatchDeps {
  /** Resolves once the appointment has been uploaded, false on timeout. */
  waitForUpload: (signal: AbortSignal) => Promise<boolean>;
  postJson: (
    url: string,
    body: unknown,
    signal: AbortSignal,
  ) => Promise<{ status: number; body: Record<string, unknown> }>;
  endpointUrl: string;
}

/**
 * Request the confirmation for an appointment.
 *
 * Retries only the `pending` (not-yet-synced) case, and only a bounded number
 * of times — every other outcome is final. Safe to call repeatedly: the
 * endpoint is idempotent on the appointment's client_reference.
 */
/**
 * Wait for the appointment to reach Postgres, then POST it.
 *
 * Shared by BOTH entry points. The wait is the whole point: web-admin writes
 * to local SQLite first, so calling the backend before PowerSync has uploaded
 * gets `appointment_not_found`. Retries only the `pending` case, bounded —
 * every other outcome is final. Safe to repeat: both endpoints are idempotent
 * on their own client_reference.
 */
async function dispatchWithSyncWait(
  deps: DispatchDeps,
  body: Record<string, unknown>,
  signal: AbortSignal,
  opts: {
    pendingRetries?: number;
    pendingDelayMs?: number;
    sleep?: (ms: number) => Promise<void>;
  } = {},
): Promise<AppointmentBookedOutcome> {
  const pendingRetries = opts.pendingRetries ?? 3;
  const pendingDelayMs = opts.pendingDelayMs ?? 1500;
  const sleep = opts.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));

  if (signal.aborted) return { status: 'cancelled' };

  const uploaded = await deps.waitForUpload(signal);
  if (signal.aborted) return { status: 'cancelled' };
  if (!uploaded) return { status: 'timeout' };

  for (let attempt = 0; attempt <= pendingRetries; attempt++) {
    if (signal.aborted) return { status: 'cancelled' };
    try {
      const res = await deps.postJson(deps.endpointUrl, body, signal);
      const status = typeof res.body.status === 'string' ? res.body.status : '';

      // `pending` means the row still is not visible server-side — the only
      // retryable outcome.
      if (res.status === 202 || status === 'pending') {
        if (attempt === pendingRetries) return { status: 'timeout' };
        await sleep(pendingDelayMs);
        continue;
      }
      if (status === 'sent') return { status: 'sent' };
      if (status === 'queued') return { status: 'queued' };
      if (status === 'skipped') {
        return { status: 'skipped', reason: String(res.body.reason ?? 'unknown') };
      }
      if (res.status >= 400) {
        return {
          status: 'error',
          message: String(res.body.error ?? `Request failed (${res.status})`),
        };
      }
      return { status: 'queued' };
    } catch (err) {
      if (signal.aborted) return { status: 'cancelled' };
      return { status: 'error', message: err instanceof Error ? err.message : 'Request failed' };
    }
  }
  return { status: 'timeout' };
}

/** PRD.299 — booking confirmation. `force` is a deliberate operator resend. */
export async function dispatchAppointmentBooked(
  deps: DispatchDeps,
  appointmentId: string,
  signal: AbortSignal,
  opts: {
    pendingRetries?: number;
    pendingDelayMs?: number;
    sleep?: (ms: number) => Promise<void>;
    force?: boolean;
  } = {},
): Promise<AppointmentBookedOutcome> {
  const body = opts.force ? { appointmentId, force: true } : { appointmentId };
  return dispatchWithSyncWait(deps, body, signal, opts);
}

/** Whether an outcome is worth offering a Retry for. */
export function isRetryable(outcome: AppointmentBookedOutcome): boolean {
  return outcome.status === 'timeout' || outcome.status === 'error';
}

/** User-facing line for an outcome. `null` means show nothing at all. */
export function outcomeMessage(
  outcome: AppointmentBookedOutcome,
  opts: { verbose?: boolean } = {},
): string | null {
  switch (outcome.status) {
    case 'sent':
      return '"Appointment Booked" sent';
    case 'queued':
      return '"Appointment Booked" queued';
    case 'cancelled':
      return '"Appointment Booked" cancelled';
    case 'timeout':
      return 'Couldn\'t send "Appointment Booked" — not synced yet';
    case 'error':
      return 'Couldn\'t send "Appointment Booked"';
    case 'skipped':
      return skippedMessage(outcome.reason, opts.verbose === true);
  }
}

/**
 * Skips are mostly expected states, not failures. After an AUTOMATIC booking
 * only the actionable ones surface, so the flow is not nagged.
 *
 * `verbose` (a manual button press) always produces a message: pressing a
 * button and seeing nothing at all reads as broken, even when the silence is
 * technically correct.
 */
function skippedMessage(reason: string, verbose: boolean): string | null {
  switch (reason) {
    case 'not_opted_in':
      return 'Not sent — patient has not opted in to WhatsApp';
    case 'opted_out':
      return 'Not sent — patient opted out of WhatsApp';
    case 'no_phone':
      return 'Not sent — no valid mobile number';
    case 'template_missing':
    case 'template_inactive':
    case 'template_not_whatsapp_meta':
    case 'template_mapping_invalid':
      return 'Not sent — "Appointment Booked" is not configured for this clinic';
    // Silent after an automatic booking (it means the send already happened),
    // but a MANUAL press needs to hear it — otherwise the button looks broken.
    case 'already_dispatched':
      return '"Appointment Booked" was already sent for this appointment';
    case 'appointment_in_past':
      return verbose ? 'Not sent — this appointment has already started' : null;
    case 'source_not_web_admin':
      return verbose ? 'Not sent — this appointment was not booked from PowerCare' : null;
    case 'internal_error':
      return 'Not sent — something went wrong. Please try again.';
    default:
      // Never leave a manual press with no explanation, even for a reason this
      // build does not know about yet.
      return verbose ? `Not sent (${reason})` : null;
  }
}

/* ------------------------------------------------------------------ */
/*  PRD.300 — same-day reminder trigger (path B)                       */
/*                                                                     */
/*  Fired right after the booking confirmation, on the same waited-for */
/*  sync. The backend decides everything: whether it is today, whether */
/*  the clinic-local clock has passed 06:00, lead time, eligibility    */
/*  and idempotency against the morning batch. The client only says    */
/*  "this appointment was just created".                               */
/* ------------------------------------------------------------------ */

export async function dispatchSameDayReminder(
  deps: DispatchDeps,
  appointmentId: string,
  signal: AbortSignal,
  opts: {
    pendingRetries?: number;
    pendingDelayMs?: number;
    sleep?: (ms: number) => Promise<void>;
  } = {},
): Promise<AppointmentBookedOutcome> {
  if (!deps.endpointUrl) return { status: 'skipped', reason: 'not_configured' };
  return dispatchWithSyncWait(deps, { appointmentId, trigger: 'same_day' }, signal, opts);
}
